import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";
import Swal from "sweetalert2";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { useCities, useLocalities, useProvinces } from "../../Hooks/use-locations";

const NO_NEIGHBORHOOD = "No aplica";

/** Dirección actual de la orden en la forma del formulario. */
const initialForm = (order) => {
  const s = order.shipping_address?.address ? order.shipping_address : null;
  const a = order.address_id || {};
  const ref = (snap, populated) => ({
    id: snap?.id || populated?.id || populated?._id || "",
    name: snap?.name || populated?.name || "",
  });

  return {
    name: s?.name || a.name || "",
    address: s?.address || a.address || "",
    country: ref(s?.country, a.country_id),
    province_id: ref(s?.province, a.province_id).id,
    city_id: ref(s?.city, a.city_id).id,
    postal_code: s?.postal_code || a.postal_code || "",
    neighborhood: s?.neighborhood || a.neighborhood || NO_NEIGHBORHOOD,
    neighborhood_code: s?.neighborhood_code || a.neighborhood_code || "",
    observations: s?.observations || a.observations || "",
    receiver: {
      type: (s || a).receiver?.type === "other" ? "other" : "self",
      name: (s || a).receiver?.name || "",
      document: (s || a).receiver?.document || "",
      phone: (s || a).receiver?.phone || "",
    },
  };
};

/**
 * Edita la dirección de envío guardada en la orden. No cambia la dirección del
 * cliente: sólo la de esta orden, que es la que usan el despacho y las guías.
 */
export const EditShippingAddressModal = ({ order, isOpen, toggle, onDone }) => {
  const { patchData } = useDataFetch();
  const [form, setForm] = useState(() => initialForm(order));
  const [error, setError] = useState("");

  const { provinces } = useProvinces(form.country.id);
  const { cities } = useCities(form.province_id);
  const { localities, isLoading: loadingLocalities } = useLocalities(
    form.province_id,
    form.city_id
  );

  const hasGuide = !!(order.interrapidisimo_guide || order.servientrega_guide);

  const { mutate, isPending } = patchData(`/order/${order.id || order._id}/shipping-address`, {}, {
    onSuccess: () => {
      Swal.fire("Dirección actualizada", "Se guardó la nueva dirección de la orden.", "success");
      toggle();
      onDone?.();
    },
    onError: (e) => setError(e.message),
  });

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setReceiver = (field, value) =>
    setForm((prev) => ({ ...prev, receiver: { ...prev.receiver, [field]: value } }));

  const handleSubmit = async () => {
    setError("");
    const province = provinces.find((p) => p.id === form.province_id);
    const city = cities.find((c) => c.id === form.city_id);

    if (!form.address.trim() || !province || !city) {
      setError("Dirección, departamento y ciudad son obligatorios.");
      return;
    }
    if (
      form.receiver.type === "other" &&
      !(form.receiver.name && form.receiver.document && form.receiver.phone)
    ) {
      setError("Completa nombre, documento y teléfono de quien recibe.");
      return;
    }

    if (hasGuide) {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "La orden ya tiene guía",
        text: "La guía generada no se modifica: queda con la dirección anterior. ¿Guardar igual?",
        showCancelButton: true,
        confirmButtonText: "Sí, guardar",
        cancelButtonText: "Cancelar",
      });
      if (!isConfirmed) return;
    }

    mutate({
      name: form.name,
      address: form.address,
      country: form.country,
      province: { id: province.id, name: province.name },
      city: { id: city.id, name: city.name },
      postal_code: form.postal_code,
      neighborhood: form.neighborhood,
      neighborhood_code: form.neighborhood_code,
      observations: form.observations,
      receiver: form.receiver,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
      <ModalHeader toggle={toggle}>Editar dirección de la orden</ModalHeader>
      <ModalBody>
        <Alert color="info" className="py-2">
          Sólo cambia la dirección de esta orden. La dirección guardada del cliente no se modifica.
          El costo del envío no se recalcula.
        </Alert>
        {error && <Alert color="danger" className="py-2">{error}</Alert>}

        <Row>
          <Col md={4}>
            <FormGroup>
              <Label>Alias</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={8}>
            <FormGroup>
              <Label>Dirección *</Label>
              <Input
                value={form.address}
                maxLength={50}
                onChange={(e) => set("address", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label>País</Label>
              <Input value={form.country.name || "-"} disabled />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Departamento *</Label>
              <Input
                type="select"
                value={form.province_id}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    province_id: e.target.value,
                    city_id: "",
                    neighborhood: NO_NEIGHBORHOOD,
                    neighborhood_code: "",
                  }))
                }
              >
                <option value="">Selecciona</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Ciudad *</Label>
              <Input
                type="select"
                value={form.city_id}
                disabled={!form.province_id}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    city_id: e.target.value,
                    neighborhood: NO_NEIGHBORHOOD,
                    neighborhood_code: "",
                  }))
                }
              >
                <option value="">Selecciona</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label>Barrio / Corregimiento</Label>
              <Input
                type="select"
                value={form.neighborhood_code}
                disabled={!form.city_id || loadingLocalities}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    neighborhood_code: e.target.value,
                    neighborhood:
                      localities.find((l) => l.id === e.target.value)?.name || NO_NEIGHBORHOOD,
                  }))
                }
              >
                <option value="">{loadingLocalities ? "Cargando..." : NO_NEIGHBORHOOD}</option>
                {localities.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Código postal</Label>
              <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>¿Quién recibe?</Label>
              <Input
                type="select"
                value={form.receiver.type}
                onChange={(e) => setReceiver("type", e.target.value)}
              >
                <option value="self">El cliente</option>
                <option value="other">Otra persona</option>
              </Input>
            </FormGroup>
          </Col>

          {form.receiver.type === "other" && (
            <>
              <Col md={4}>
                <FormGroup>
                  <Label>Nombre de quien recibe *</Label>
                  <Input value={form.receiver.name} onChange={(e) => setReceiver("name", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Documento *</Label>
                  <Input value={form.receiver.document} onChange={(e) => setReceiver("document", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Teléfono *</Label>
                  <Input value={form.receiver.phone} onChange={(e) => setReceiver("phone", e.target.value)} />
                </FormGroup>
              </Col>
            </>
          )}

          <Col md={12}>
            <FormGroup>
              <Label>Observaciones</Label>
              <Input
                type="textarea"
                rows={3}
                maxLength={250}
                value={form.observations}
                onChange={(e) => set("observations", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={isPending}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isPending}>
          {isPending ? <Spinner size="sm" /> : "Guardar dirección"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
